import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HeroParticles from './HeroParticles';

/* ─────────────────────────────────────────────────────────────────────────
   WordReveal  — animates each word of a string sliding up one by one
───────────────────────────────────────────────────────────────────────── */
interface WordRevealProps {
  text: string;
  startDelay?: number;
  stagger?: number;
}

function WordReveal({ text, startDelay = 300, stagger = 130 }: WordRevealProps) {
  const words = text.split(' ');
  const [count, setCount] = useState(0);

  useEffect(() => {
    const outer = setTimeout(() => {
      let c = 0;
      const iv = setInterval(() => {
        c++;
        setCount(c);
        if (c >= words.length) clearInterval(iv);
      }, stagger);
      return () => clearInterval(iv);
    }, startDelay);
    return () => clearTimeout(outer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {words.map((word, i) => (
        <span
          key={i}
          className="inline-block"
          style={{
            opacity: i < count ? 1 : 0,
            transform: i < count ? 'translateY(0px)' : 'translateY(24px)',
            // No blur — avoids compositing layer per word
            transition: 'opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)',
            marginRight: i < words.length - 1 ? '0.28em' : '0',
          }}
        >
          {word}
        </span>
      ))}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   TypewriterText — types out text char-by-char then fades into shimmer
───────────────────────────────────────────────────────────────────────── */
interface TypewriterProps {
  text: string;
  startDelay?: number;
  speed?: number;
}

function TypewriterText({ text, startDelay = 700, speed = 62 }: TypewriterProps) {
  const [displayed, setDisplayed] = useState('');
  const [phase, setPhase] = useState<'idle' | 'typing' | 'shimmer'>('idle');
  const [cursorOn, setCursorOn] = useState(true);

  // Trigger typing after delay
  useEffect(() => {
    const t = setTimeout(() => setPhase('typing'), startDelay);
    return () => clearTimeout(t);
  }, [startDelay]);

  // Type characters one by one
  useEffect(() => {
    if (phase !== 'typing') return;
    let i = 0;
    const iv = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(iv);
        // Brief pause → transition to shimmer
        setTimeout(() => setPhase('shimmer'), 750);
      }
    }, speed);
    return () => clearInterval(iv);
  }, [phase, text, speed]);

  // Cursor blink while typing
  useEffect(() => {
    if (phase === 'shimmer') return;
    const iv = setInterval(() => setCursorOn((v) => !v), 520);
    return () => clearInterval(iv);
  }, [phase]);

  const showCursor = phase === 'idle' || phase === 'typing';

  return (
    <span
      className={phase === 'shimmer' ? 'hero-shimmer-text' : 'text-electric'}
      style={{
        display: 'inline',
        transition: 'opacity 0.35s ease',
      }}
    >
      {phase === 'idle' ? '\u00A0' : displayed}
      {showCursor && (
        <span
          style={{
            opacity: cursorOn ? 1 : 0,
            transition: 'opacity 0.08s',
            fontWeight: 200,
            marginLeft: '2px',
          }}
        >
          |
        </span>
      )}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Parallax rates — lower = slower = further away
───────────────────────────────────────────────────────────────────────── */
const RATE_BG   = 0.30;
const RATE_GRID = 0.16;
const RATE_ORBS = 0.42;

const stats = [
  { value: '99.9%', labelKey: 'stat_availability' },
  { value: '48h',   labelKey: 'stat_install' },
  { value: '24/7',  labelKey: 'stat_support' },
];

/* ─────────────────────────────────────────────────────────────────────────
   Hero
───────────────────────────────────────────────────────────────────────── */
export default function Hero() {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();

  const bgRef   = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const orbsRef = useRef<HTMLDivElement>(null);

  // Entrance
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Multi-layer parallax at 60 fps via direct DOM mutation
  useEffect(() => {
    let rafId = 0;
    let lastY  = -1;

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y !== lastY) {
          lastY = y;
          if (bgRef.current)   bgRef.current.style.transform   = `translateY(${y * RATE_BG}px)`;
          if (gridRef.current) gridRef.current.style.transform = `translateY(${y * RATE_GRID}px)`;
          if (orbsRef.current) orbsRef.current.style.transform = `translateY(${y * RATE_ORBS}px)`;
        }
        rafId = 0;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* LAYER 1 — Background image (slowest) */}
      <div
        ref={bgRef}
        className="absolute left-0 right-0 will-change-transform"
        style={{ top: '-30%', height: '160%' }}
      >
        <img
          src="https://readdy.ai/api/search-image?query=dark%20navy%20blue%20tech%20office%20environment%20with%20glowing%20electric%20blue%20digital%20screens%20showing%20data%20dashboards%20and%20analytics%2C%20modern%20minimalist%20workspace%2C%20dark%20atmosphere%20with%20cyan%20and%20electric%20blue%20lighting%20accents%2C%20professional%20business%20technology%20environment%2C%20high-end%20ultrawide%20monitors%2C%20futuristic%20but%20clean%2C%20cinematic%20depth%20of%20field&width=1920&height=1080&seq=hero001&orientation=landscape"
          alt="InnoFlex tecnología para negocios"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(2,8,23,0.82), rgba(2,8,23,0.70), rgba(2,8,23,0.94))' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(2,8,23,0.55), transparent, rgba(2,8,23,0.55))' }} />
      </div>

      {/* LAYER 2 — Grid lines */}
      <div
        ref={gridRef}
        className="absolute inset-0 opacity-10 pointer-events-none will-change-transform"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,207,255,0.30) 1px, transparent 1px), linear-gradient(90deg, rgba(0,207,255,0.30) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      {/* LAYER 3 — Canvas particles */}
      <HeroParticles />

      {/* LAYER 4 — Glow orbs — no blur filter, use pure radial-gradient opacity */}
      <div ref={orbsRef} className="absolute inset-0 pointer-events-none overflow-hidden will-change-transform">
        <div className="absolute rounded-full hero-orb-1" style={{ width: '700px', height: '700px', top: '5%', left: '2%', background: 'radial-gradient(circle, rgba(0,207,255,0.09) 0%, transparent 60%)' }} />
        <div className="absolute rounded-full hero-orb-2" style={{ width: '600px', height: '600px', bottom: '2%', right: '4%', background: 'radial-gradient(circle, rgba(0,255,157,0.07) 0%, transparent 60%)' }} />
        <div className="absolute rounded-full hero-orb-3" style={{ width: '400px', height: '400px', top: '35%', right: '22%', background: 'radial-gradient(circle, rgba(0,207,255,0.06) 0%, transparent 60%)' }} />
      </div>

      {/* LAYER 5 — Foreground content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-5 pt-20 pb-12 sm:pt-24 md:pt-28 md:pb-20 text-center">

        {/* Animated headline — word reveal + typewriter */}
        <h1 className="aw font-heading text-2xl sm:text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-4 md:mb-6 max-w-5xl mx-auto px-2 sm:px-0">
          {/* Line 1: word-by-word slide-up */}
          <span className="block">
            <WordReveal text={t('hero_headline_1')} startDelay={350} stagger={140} />
          </span>
          {/* Line 2: typewriter → shimmer */}
          <span className="block mt-1">
            <TypewriterText
              text={t('hero_headline_2')}
              startDelay={900}
              speed={60}
            />
          </span>
        </h1>

        {/* Everything else fades in together */}
        <div
          className="transition-all duration-1000"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transitionDelay: '1800ms',
          }}
        >
          {/* Subtitle */}
          <p className="aw-70 font-body text-sm md:text-base lg:text-lg max-w-xl mx-auto mb-7 md:mb-10 leading-relaxed px-4 sm:px-2">
            {t('hero_sub')}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-5 md:mb-7 px-4 sm:px-0">
            <a
              href="#book-demo"
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-gradient-to-r from-electric to-emerald-400 hover:from-electric/90 hover:to-emerald-400/90 text-navy-950 font-extrabold text-sm md:text-base px-6 md:px-8 py-3.5 md:py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap font-body ring-2 ring-electric/30 hover:ring-electric/60 hover:scale-105 active:scale-95 touch-manipulation"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-calendar-schedule-line text-lg" />
              </div>
              {t('hero_cta_demo')}
            </a>
            <a
              href="#services"
              className="aw w-full sm:w-auto flex items-center justify-center gap-2 border font-semibold text-sm md:text-base px-6 md:px-8 py-3.5 md:py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap font-body hover:bg-white/10 touch-manipulation"
              style={{ borderColor: 'rgba(255,255,255,0.20)' }}
            >
              {t('hero_cta_services')}
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </a>
          </div>

          {/* Already a client */}
          <div className="flex items-center justify-center mb-8 md:mb-16 px-4">
            <a
              href="https://ebm-app.onrender.com/auth/login"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2.5 cursor-pointer border border-white/30 hover:border-electric/60 bg-white/5 hover:bg-electric/10 px-5 py-2.5 rounded-full transition-all duration-200"
            >
              <div className="w-4 h-4 flex items-center justify-center text-electric flex-shrink-0">
                <i className="ri-user-3-line text-sm" />
              </div>
              <span className="aw font-body text-xs sm:text-sm font-medium">
                {t('hero_already_client')}
              </span>
              <div className="w-4 h-4 flex items-center justify-center text-electric flex-shrink-0 group-hover:translate-x-0.5 transition-transform duration-200">
                <i className="ri-arrow-right-line text-sm" />
              </div>
            </a>
          </div>

          {/* Stats */}
          <div className="flex flex-row flex-wrap items-start justify-center gap-x-6 gap-y-4 sm:gap-x-12 sm:gap-y-0 px-2">
            {stats.map((stat, i) => (
              <div
                key={stat.labelKey}
                className="text-center min-w-0"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(16px)',
                  transition: 'opacity 0.7s ease, transform 0.7s ease',
                  transitionDelay: `${2000 + i * 150}ms`,
                }}
              >
                <div className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-electric">
                  {stat.value}
                </div>
                <div className="aw-60 font-body text-xs md:text-sm mt-0.5 md:mt-1 leading-snug max-w-[80px] sm:max-w-none mx-auto">
                  {t(stat.labelKey)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce z-10 cursor-pointer bg-transparent border-none outline-none"
        aria-label="Scroll hacia abajo"
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-down-line text-base" />
        </div>
      </button>
    </section>
  );
}
